/**
 * The formatted-text editor and its reader, for rule proposals.
 *
 * Eight commands: bold, italic, underline, strikethrough, bulleted list, numbered list, link,
 * table. That is the list the league asked for and it is worth not exceeding - headings and
 * colours in a document that gets read aloud in a meeting are decoration, and every extra control
 * is another thing to explain to nine people at once.
 *
 * IT IS BUILT ON `execCommand`, WHICH IS DEPRECATED. That is a real cost and it was taken
 * deliberately. The replacement - a document model, a selection model, an input-event pipeline -
 * is the thing editor frameworks exist to provide, and pulling one in would put a quarter of a
 * megabyte into a build whose whole purpose is to open on a phone with no signal. `execCommand`
 * is still implemented by every browser this league uses and its failure mode is a button that
 * does nothing, not a corrupted document.
 *
 * THE TABLE COMMANDS DO NOT USE IT. There is no `execCommand` for "add a column", so rows and
 * columns are edited as DOM directly and the result is read back out through the same `emit` as
 * every keystroke. That is the part of this file most likely to need care later: it is the work
 * an editor framework would have done, and it is where the no-dependency decision is felt.
 *
 * The editable is UNCONTROLLED on purpose. Writing `innerHTML` on every keystroke moves the caret
 * to the end of the document, which is the single most common way a hand-rolled rich text field
 * gets shipped broken. The DOM is written to only when the incoming value genuinely differs from
 * what is already displayed, which happens when the editor is opened, not while it is being typed
 * in.
 */

import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { parseRich, type RichNode, safeHref, sanitizeRich } from '../domain/richText.js';
import { anchorAround, caretAfter, linkRange, unwrap } from './richLink.js';

type Cmd = 'bold' | 'italic' | 'underline' | 'strikeThrough'
  | 'insertUnorderedList' | 'insertOrderedList';

const TOOLS: { cmd: Cmd; label: string; hint: string; glyph: ReactNode }[] = [
  { cmd: 'bold', label: 'Bold', hint: 'Bold (Cmd-B)', glyph: <b>B</b> },
  { cmd: 'italic', label: 'Italic', hint: 'Italic (Cmd-I)', glyph: <i>I</i> },
  { cmd: 'underline', label: 'Underline', hint: 'Underline (Cmd-U)', glyph: <u>U</u> },
  { cmd: 'strikeThrough', label: 'Strikethrough', hint: 'Strikethrough', glyph: <s>S</s> },
  { cmd: 'insertUnorderedList', label: 'Bulleted list', hint: 'Bulleted list', glyph: <ListIcon /> },
  { cmd: 'insertOrderedList', label: 'Numbered list', hint: 'Numbered list', glyph: <ListIcon ordered /> },
];

/* -- finding where the caret is --------------------------------------------------------------- */

/** The nearest ancestor of the caret matching `is`, stopping at the editor's own boundary. */
function nearest<T extends Element>(
  root: HTMLElement | null, is: (n: Node) => n is T,
): T | null {
  const sel = document.getSelection();
  if (!root || !sel || !sel.rangeCount) return null;
  let n: Node | null = sel.getRangeAt(0).startContainer;
  while (n && n !== root) {
    if (is(n)) return n;
    n = n.parentNode;
  }
  return null;
}

const isCell = (n: Node): n is HTMLTableCellElement =>
  n.nodeName === 'TD' || n.nodeName === 'TH';
const isAnchor = (n: Node): n is HTMLAnchorElement => n.nodeName === 'A';

/** Put the caret at the start of an element, which is where somebody wants it after a table op. */
function caretInto(el: Element): void {
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(true);
  const sel = document.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
}

export function RichEditor(
  { value, onChange, placeholder, label }:
  { value: string; onChange: (html: string) => void; placeholder?: string; label?: string },
) {
  const ref = useRef<HTMLDivElement | null>(null);
  const saved = useRef<Range | null>(null);
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [empty, setEmpty] = useState(!value);
  const [inTable, setInTable] = useState(false);
  const [link, setLink] = useState<{ url: string; existing: boolean } | null>(null);
  const [refused, setRefused] = useState(false);

  /*
   * Only ever write the DOM when it disagrees with the value we were handed. See the file note:
   * the alternative is a caret that jumps to the end of the document on every character.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.innerHTML !== value) el.innerHTML = value;
    setEmpty(!el.textContent?.trim() && !el.querySelector('li, td, th'));
  }, [value]);

  const refresh = useCallback(() => {
    const state: Record<string, boolean> = {};
    for (const t of TOOLS) {
      try { state[t.cmd] = document.queryCommandState(t.cmd); } catch { state[t.cmd] = false; }
    }
    setActive(state);
    setInTable(Boolean(nearest(ref.current, isCell)));
  }, []);

  useEffect(() => {
    const on = () => {
      const el = ref.current;
      if (el && document.activeElement === el) refresh();
    };
    document.addEventListener('selectionchange', on);
    return () => document.removeEventListener('selectionchange', on);
  }, [refresh]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    setEmpty(!el.textContent?.trim() && !el.querySelector('li, td, th'));
    onChange(el.innerHTML);
  };

  /*
   * `focusBack` used to live here — refocus the editor, then hand the saved range back to the
   * selection and hope. Nothing needs it now: the link path operates on the range itself, and
   * every other command runs while the editor still has focus. Asking an engine to restore a
   * selection was the bug, not the fix for it.
   */
  const remember = () => {
    const sel = document.getSelection();
    saved.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
  };

  const exec = (cmd: Cmd) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    /*
     * `styleWithCSS: false` is what makes this emit `<b>` rather than `<span style="font-weight">`.
     * Without it Chrome produces spans, and although the sanitiser now reads a span's style and
     * recovers the mark, the stored markup would be a diff nobody can read.
     */
    try { document.execCommand('styleWithCSS', false, 'false'); } catch { /* Firefox throws */ }
    document.execCommand(cmd);
    refresh();
    emit();
  };

  /* -- links ---------------------------------------------------------------------------------- */

  const openLink = () => {
    remember();
    const a = nearest(ref.current, isAnchor);
    setRefused(false);
    setLink({ url: a?.getAttribute('href') ?? '', existing: Boolean(a) });
  };

  const applyLink = () => {
    const el = ref.current;
    const url = safeHref(link?.url ?? '');
    /*
     * REFUSED IN THE FIELD, not silently on save. The sanitiser would drop a `javascript:` href
     * anyway, but a link that vanishes an hour later when somebody presses Submit is a bug report;
     * a field that says no while you are looking at it is a rule.
     */
    if (!url) { setRefused(true); return; }
    if (!el) return;

    /*
     * THE SAVED RANGE IS THE INPUT, not the live selection — see `richLink.ts` for the whole of
     * why. Short version: the URL field took focus, focusing a form control clears the document
     * selection, and whether it comes back when the editor is refocused is an engine's opinion.
     * Safari's opinion is the one every manager in this league is running.
     */
    const range = saved.current;
    if (!range) return;
    el.focus();
    const { anchor } = linkRange(range, url, el);
    if (anchor) caretAfter(anchor);
    saved.current = null;
    setLink(null);
    refresh();
    emit();
  };

  const dropLink = () => {
    const el = ref.current;
    const range = saved.current;
    if (!el || !range) return;
    el.focus();
    const a = anchorAround(range.startContainer, el) ?? anchorAround(range.endContainer, el);
    if (a) unwrap(a);
    saved.current = null;
    setLink(null);
    refresh();
    emit();
  };

  /* -- tables --------------------------------------------------------------------------------- */

  const table = () => nearest(ref.current, isCell)?.closest('table') ?? null;

  const insertTable = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    /*
     * CELLS ARE EMPTY, NOT PREFILLED. The traditional trick is a non-breaking space in every cell
     * so there is something to click on, and it works - but the space is real, it stays in front
     * of whatever gets typed, and it comes out the other end in the minutes as a column of values
     * that all begin with a space. The cells are given a minimum size in CSS instead, which
     * solves the clicking without putting anything in the document.
     */
    const head = '<tr><th></th><th></th><th></th></tr>';
    const row = '<tr><td></td><td></td><td></td></tr>';
    document.execCommand('insertHTML', false,
      `<table><thead>${head}</thead><tbody>${row}${row}</tbody></table><p><br></p>`);
    const first = el.querySelector('table th');
    if (first) caretInto(first);
    refresh();
    emit();
  };

  const addRow = () => {
    const cell = nearest(ref.current, isCell);
    const row = cell?.closest('tr');
    if (!cell || !row) return;
    const fresh = document.createElement('tr');
    for (let k = 0; k < row.cells.length; k += 1) fresh.appendChild(document.createElement('td'));
    /* A new row always lands in the body, even when the caret was in the header. */
    const body = row.closest('table')?.tBodies[0];
    if (row.parentElement?.tagName === 'THEAD' && body) body.insertBefore(fresh, body.firstChild);
    else row.parentElement?.insertBefore(fresh, row.nextSibling);
    caretInto(fresh.cells[0]!);
    refresh();
    emit();
  };

  const addCol = () => {
    const cell = nearest(ref.current, isCell);
    const t = table();
    if (!cell || !t) return;
    const at = cell.cellIndex + 1;
    for (const row of Array.from(t.rows)) {
      const made = document.createElement(row.parentElement?.tagName === 'THEAD' ? 'th' : 'td');
      row.insertBefore(made, row.cells[at] ?? null);
    }
    const here = t.rows[cell.closest('tr')!.rowIndex]?.cells[at];
    if (here) caretInto(here);
    refresh();
    emit();
  };

  const dropRow = () => {
    const row = nearest(ref.current, isCell)?.closest('tr');
    const t = table();
    if (!row || !t) return;
    /* Removing the last row leaves an empty shell that renders as a hairline. Take the table. */
    if (t.rows.length <= 1) { t.remove(); } else { row.remove(); }
    refresh();
    emit();
  };

  const dropCol = () => {
    const cell = nearest(ref.current, isCell);
    const t = table();
    if (!cell || !t) return;
    const at = cell.cellIndex;
    if ((t.rows[0]?.cells.length ?? 0) <= 1) { t.remove(); } else {
      for (const row of Array.from(t.rows)) row.cells[at]?.remove();
    }
    refresh();
    emit();
  };

  const dropTable = () => {
    table()?.remove();
    refresh();
    emit();
  };

  /*
   * TAB MOVES BETWEEN CELLS, and from the last cell it makes a new row. That is what every
   * spreadsheet and every word processor does, and a table you cannot Tab through is a table
   * people fill in with the mouse, one click at a time.
   */
  const tabCell = (back: boolean) => {
    const cell = nearest(ref.current, isCell);
    const t = table();
    if (!cell || !t) return false;
    const cells = Array.from(t.querySelectorAll('th, td'));
    const at = cells.indexOf(cell);
    const next = cells[at + (back ? -1 : 1)];
    if (next) { caretInto(next); return true; }
    if (back) return true;
    addRow();
    return true;
  };

  /*
   * PASTE IS THE REAL INPUT. Half of these arrive pasted out of Google Docs or an email, carrying
   * fonts, colours, tracked-change markup and occasionally a whole table. Letting the browser
   * insert that and sanitising later would show the manager formatting that vanishes when he
   * saves; sanitising here means what he sees pasted is what is stored.
   */
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const clean = html
      ? sanitizeRich(html)
      : text.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!))
        .replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, clean);
    emit();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab' && tabCell(e.shiftKey)) { e.preventDefault(); emit(); return; }
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'b') { e.preventDefault(); exec('bold'); }
    if (k === 'i') { e.preventDefault(); exec('italic'); }
    if (k === 'u') { e.preventDefault(); exec('underline'); }
    if (k === 'k') { e.preventDefault(); openLink(); }
  };

  const linked = Boolean(nearest(ref.current, isAnchor));

  return (
    <div className="richwrap">
      <div className="richbar" role="toolbar" aria-label={label ? `${label} formatting` : 'Formatting'}>
        {TOOLS.map((t) => (
          <button key={t.cmd} type="button" className={`richbtn ${active[t.cmd] ? 'on' : ''}`}
            title={t.hint} aria-label={t.label} aria-pressed={Boolean(active[t.cmd])}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(t.cmd)}>
            {t.glyph}
          </button>
        ))}
        <span className="richsep" aria-hidden />
        <button type="button" className={`richbtn ${linked ? 'on' : ''}`}
          title="Link (Cmd-K)" aria-label="Link" aria-pressed={linked}
          onMouseDown={(e) => e.preventDefault()} onClick={openLink}>
          <LinkIcon />
        </button>
        <button type="button" className="richbtn" title="Insert a table" aria-label="Insert a table"
          onMouseDown={(e) => e.preventDefault()} onClick={insertTable}>
          <TableIcon />
        </button>
      </div>

      {link && (
        <div className="richlink">
          <input value={link.url} autoFocus placeholder="https://…  or  tim@example.com"
            aria-label="Link address"
            onChange={(e) => { setLink({ ...link, url: e.target.value }); setRefused(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') { e.preventDefault(); setLink(null); }
            }} />
          <button type="button" className="btn tiny primary" onClick={applyLink}>
            {link.existing ? 'Change' : 'Add'}
          </button>
          {link.existing && (
            <button type="button" className="linkbtn tiny" onClick={dropLink}>remove</button>
          )}
          <button type="button" className="linkbtn tiny" onClick={() => setLink(null)}>cancel</button>
          {refused && (
            <p className="note bad richlinkbad">
              Only web addresses and email links can go in a proposal.
            </p>
          )}
        </div>
      )}

      {inTable && (
        <div className="richtablebar" role="toolbar" aria-label="Table">
          <span className="richtablelabel">Table</span>
          <button type="button" className="linkbtn tiny" onMouseDown={(e) => e.preventDefault()}
            onClick={addRow}>+ row</button>
          <button type="button" className="linkbtn tiny" onMouseDown={(e) => e.preventDefault()}
            onClick={addCol}>+ column</button>
          <button type="button" className="linkbtn tiny" onMouseDown={(e) => e.preventDefault()}
            onClick={dropRow}>remove row</button>
          <button type="button" className="linkbtn tiny" onMouseDown={(e) => e.preventDefault()}
            onClick={dropCol}>remove column</button>
          <button type="button" className="linkbtn tiny" onMouseDown={(e) => e.preventDefault()}
            onClick={dropTable}>delete table</button>
        </div>
      )}

      <div className="richbody" ref={ref} contentEditable suppressContentEditableWarning
        role="textbox" aria-multiline="true" aria-label={label ?? 'Formatted text'}
        data-empty={empty ? '' : undefined} data-placeholder={placeholder}
        onInput={emit} onBlur={emit} onPaste={onPaste} onKeyDown={onKeyDown}
        onKeyUp={refresh} onMouseUp={refresh} />
    </div>
  );
}

/**
 * Formatted text, rendered.
 *
 * Renders through React elements rather than `dangerouslySetInnerHTML` - see `parseRich`. Old
 * plain-text proposals come through here unchanged and still read correctly, because
 * `sanitizeRich` leaves text alone and the CSS preserves the line breaks that were doing the
 * formatting before this existed.
 */
export function RichView({ html, className }: { html: string; className?: string }) {
  if (!html?.trim()) return null;
  return <div className={`richtext ${className ?? ''}`}>{render(parseRich(html), 'r')}</div>;
}

function render(nodes: readonly RichNode[], key: string): ReactNode {
  return nodes.map((n, i) => {
    const k = `${key}-${i}`;
    if (typeof n === 'string') return <Fragment key={k}>{n}</Fragment>;
    if (n.tag === 'br') return <br key={k} />;

    /*
     * `rel` and `target` are added HERE rather than stored, because they are a property of how
     * this app chooses to open a link, not of what the author wrote. `noopener` is the one that
     * matters: without it the opened page gets a handle on this one through `window.opener`.
     */
    if (n.tag === 'a') {
      return (
        <a key={k} href={n.href} target="_blank" rel="noopener noreferrer">
          {render(n.children, k)}
        </a>
      );
    }

    /*
     * A table gets its own scroll container. A four-column keeper grid is wider than a 390px
     * phone and something has to give: either the table scrolls inside its own box, or the whole
     * page scrolls sideways and every other screen in the app inherits the problem.
     */
    if (n.tag === 'table') {
      return (
        <div className="richtablewrap" key={k}>
          <table>{render(n.children, k)}</table>
        </div>
      );
    }

    if (n.tag === 'th' || n.tag === 'td') {
      const Cell = n.tag;
      return (
        <Cell key={k} colSpan={n.colSpan} rowSpan={n.rowSpan}>{render(n.children, k)}</Cell>
      );
    }

    const Tag = n.tag;
    return <Tag key={k}>{render(n.children, k)}</Tag>;
  });
}

/* -- toolbar glyphs --------------------------------------------------------------------------- */

/*
 * Drawn rather than typed. "bullet dash" and "1. dash" were the first attempt and both read as
 * punctuation somebody had left in the toolbar by accident; a list icon is a shape people already
 * know from every other editor they use, and at 13px a glyph pair has not the resolution to say
 * "list" instead of "dot dash".
 */
function ListIcon({ ordered }: { ordered?: boolean }) {
  return (
    <svg width="15" height="12" viewBox="0 0 15 12" aria-hidden focusable="false">
      {[1, 5.5, 10].map((y, i) => (
        <g key={y}>
          {ordered
            ? (
              <text x="0" y={y + 3.2} fontSize="4.6" fill="currentColor" fontWeight="600">
                {i + 1}
              </text>
            )
            : <circle cx="1.6" cy={y + 1.6} r="1.5" fill="currentColor" />}
          <rect x="5.6" y={y + 0.7} width="9.4" height="1.7" rx="0.85" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden focusable="false"
      fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M6.5 9.5a2.6 2.6 0 0 0 3.7 0l2.3-2.3a2.6 2.6 0 0 0-3.7-3.7l-1 1" />
      <path d="M9.5 6.5a2.6 2.6 0 0 0-3.7 0L3.5 8.8a2.6 2.6 0 0 0 3.7 3.7l1-1" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="15" height="14" viewBox="0 0 16 15" aria-hidden focusable="false">
      <rect x="0.8" y="1.3" width="14.4" height="12.4" rx="1.4"
        fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M0.8 5.6h14.4M6 5.6v8.1M11 5.6v8.1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
